// App.tsx - Updated with Higher Seeds functionality and Clear Bracket support
import React, { useState, useCallback, useMemo } from 'react';
import { ConfigProvider } from './context/ConfigContext';
import { isBracketComplete, getTeamSeed } from './utils/bracketUtils';
import { WinnerWithMetadata, WinnerSource } from './types/types';
import ResponsiveLayout from './components/ResponsiveLayout';
import teamsData from './data/teams.json';

// Import teams data for higher seeds logic
const initialTeams = teamsData as any[];

const App: React.FC = () => {
  // Primary state - WinnerWithMetadata array
  const [winnersWithMetadata, setWinnersWithMetadata] = useState<WinnerWithMetadata[]>([]);
  const [playerPicks, setPlayerPicks] = useState<string>('');
  const [playerNames, setPlayerNames] = useState<string[]>([]);
  const [selectedPlayer, setSelectedPlayer] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  
  // Track changes for unified interface
  const [hasUserMadeChanges, setHasUserMadeChanges] = useState<boolean>(false);
  
  // Force update counter to ensure SimulationSection detects changes
  const [forceArrayUpdate, setForceArrayUpdate] = useState<number>(0);

  // Use useMemo to create knownWinnersArray with forced updates
  const knownWinnersArray = useMemo(() => {
    return winnersWithMetadata.map(winner => winner?.team || '');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [winnersWithMetadata, forceArrayUpdate]);

  // Helper to clear games from rounds after the first round (for Higher Seeds function)
  const clearSubsequentRounds = useCallback((winnersArray: WinnerWithMetadata[]) => {
    const result = [...winnersArray];
    
    // Clear games from rounds 1-5 (keep round 0 intact)
    for (let i = 32; i < 63; i++) { // Games 32-62 (rounds 1-5)
      // Skip games with official winners
      if (result[i] && result[i].source === WinnerSource.OFFICIAL) {
        continue;
      }
      
      // Clear non-official winners
      result[i] = undefined as any;
    }
    
    return result;
  }, []);

  // Helper to propagate winners through rounds, respecting official winners
  const advanceWinnersToNextRounds = useCallback((winnersArray: WinnerWithMetadata[]) => {
    const result = [...winnersArray];
    
    // Process 5 rounds (excluding championship)
    for (let round = 0; round < 5; round++) {
      let gamesInRound: number[];
      let nextRoundStart: number;
      
      // Define game ranges for each round
      if (round === 0) { // Round of 64 -> Round of 32
        gamesInRound = Array.from({length: 32}, (_, i) => i);
        nextRoundStart = 32;
      } else if (round === 1) { // Round of 32 -> Sweet 16
        gamesInRound = Array.from({length: 16}, (_, i) => i + 32);
        nextRoundStart = 48;
      } else if (round === 2) { // Sweet 16 -> Elite 8
        gamesInRound = Array.from({length: 8}, (_, i) => i + 48);
        nextRoundStart = 56;
      } else if (round === 3) { // Elite 8 -> Final Four
        gamesInRound = Array.from({length: 4}, (_, i) => i + 56);
        nextRoundStart = 60;
      } else { // Final Four -> Championship
        gamesInRound = Array.from({length: 2}, (_, i) => i + 60);
        nextRoundStart = 62;
      }
      
      // Push winners to next round games
      for (const gameIndex of gamesInRound) {
        if (result[gameIndex] && result[gameIndex].team) {
          const nextGameIndex = nextRoundStart + Math.floor((gameIndex - gamesInRound[0]) / 2);
          
          // Skip if the next game has an official winner
          if (result[nextGameIndex] && result[nextGameIndex].source === WinnerSource.OFFICIAL) {
            continue;
          }
          
          const winner = result[gameIndex];

          // Set winner and determine opponent for higher seed logic
          if (!result[nextGameIndex] || !result[nextGameIndex].team) {
            // Set this team as advancing
            result[nextGameIndex] = {
              team: winner.team,
              source: WinnerSource.USER
            };
          } else {
            // Both teams are set, pick higher seed
            const currentTeam = result[nextGameIndex].team;
            const newTeam = winner.team;
            
            const currentSeed = getTeamSeed(initialTeams, currentTeam);
            const newSeed = getTeamSeed(initialTeams, newTeam);
            
            if (newSeed < currentSeed) { // Lower number = higher seed
              result[nextGameIndex] = {
                team: newTeam,
                source: WinnerSource.USER
              };
            }
          }
        }
      }
    }
    
    return result;
  }, []);

  // Helper to propagate winners through rounds, picking LOWER seeds (underdogs)
  const advanceWinnersToNextRoundsLowerSeeds = useCallback((winnersArray: WinnerWithMetadata[]) => {
    const result = [...winnersArray];

    // Process 5 rounds (excluding championship)
    for (let round = 0; round < 5; round++) {
      let gamesInRound: number[];
      let nextRoundStart: number;

      // Define game ranges for each round
      if (round === 0) { // Round of 64 -> Round of 32
        gamesInRound = Array.from({length: 32}, (_, i) => i);
        nextRoundStart = 32;
      } else if (round === 1) { // Round of 32 -> Sweet 16
        gamesInRound = Array.from({length: 16}, (_, i) => i + 32);
        nextRoundStart = 48;
      } else if (round === 2) { // Sweet 16 -> Elite 8
        gamesInRound = Array.from({length: 8}, (_, i) => i + 48);
        nextRoundStart = 56;
      } else if (round === 3) { // Elite 8 -> Final Four
        gamesInRound = Array.from({length: 4}, (_, i) => i + 56);
        nextRoundStart = 60;
      } else { // Final Four -> Championship
        gamesInRound = Array.from({length: 2}, (_, i) => i + 60);
        nextRoundStart = 62;
      }

      // Push winners to next round games
      for (const gameIndex of gamesInRound) {
        if (result[gameIndex] && result[gameIndex].team) {
          const nextGameIndex = nextRoundStart + Math.floor((gameIndex - gamesInRound[0]) / 2);

          // Skip if the next game has an official winner
          if (result[nextGameIndex] && result[nextGameIndex].source === WinnerSource.OFFICIAL) {
            continue;
          }

          const winner = result[gameIndex];

          // Set winner and determine opponent for lower seed logic
          if (!result[nextGameIndex] || !result[nextGameIndex].team) {
            // Set this team as advancing
            result[nextGameIndex] = {
              team: winner.team,
              source: WinnerSource.USER
            };
          } else {
            // Both teams are set, pick lower seed (higher number = underdog)
            const currentTeam = result[nextGameIndex].team;
            const newTeam = winner.team;

            const currentSeed = getTeamSeed(initialTeams, currentTeam);
            const newSeed = getTeamSeed(initialTeams, newTeam);

            if (newSeed > currentSeed) { // Higher number = lower seed (underdog)
              result[nextGameIndex] = {
                team: newTeam,
                source: WinnerSource.USER
              };
            }
          }
        }
      }
    }

    return result;
  }, []);

  // UPDATED: Implement Higher Seeds functionality with existing logic
  const handleFillWithHigherSeeds = useCallback(() => {
    // Create a copy of current winners to preserve existing picks
    let updatedWinners = [...winnersWithMetadata];
    
    // Ensure array is proper length
    while (updatedWinners.length < 63) {
      updatedWinners.push(undefined as any);
    }
    
    // First round (Round of 64) - Select higher seed as winner for games with no winner
    for (let i = 0; i < 32; i++) {
      // Skip games with official winners
      if (updatedWinners[i] && updatedWinners[i].source === WinnerSource.OFFICIAL) {
        continue;
      }
      
      // Only set winner if not already selected by user
      if (!updatedWinners[i] || !updatedWinners[i].team) {
        // Get teams for this matchup
        const team1Index = i * 2;
        const team2Index = i * 2 + 1;
        
        if (team1Index < initialTeams.length && team2Index < initialTeams.length) {
          const team1 = initialTeams[team1Index];
          const team2 = initialTeams[team2Index];
          
          const seed1 = team1.seed;
          const seed2 = team2.seed;
          
          // Lower seed number means higher seed (1 is highest)
          if (seed1 < seed2) {
            updatedWinners[i] = {
              team: team1.name,
              source: WinnerSource.USER
            };
          } else if (seed2 < seed1) {
            updatedWinners[i] = {
              team: team2.name,
              source: WinnerSource.USER
            };
          }
          // If seeds are equal, no winner is set
        }
      }
    }
    
    // Clear subsequent rounds and propagate winners through the bracket
    updatedWinners = clearSubsequentRounds(updatedWinners);
    updatedWinners = advanceWinnersToNextRounds(updatedWinners);
    
    setWinnersWithMetadata(updatedWinners);
    setForceArrayUpdate(prev => prev + 1);
    setHasUserMadeChanges(true);
    //setErrorMessage("Higher seeds auto-fill applied to bracket");
    setTimeout(() => setErrorMessage(null), 3000);
  }, [winnersWithMetadata, clearSubsequentRounds, advanceWinnersToNextRounds]);

  // Implement Lower Seeds functionality - always pick the underdog
  const handleFillWithLowerSeeds = useCallback(() => {

    // Create a copy of current winners to preserve existing picks
    let updatedWinners = [...winnersWithMetadata];

    // Ensure array is proper length
    while (updatedWinners.length < 63) {
      updatedWinners.push(undefined as any);
    }

    // First round (Round of 64) - Select lower seed as winner for games with no winner
    for (let i = 0; i < 32; i++) {
      // Skip games with official winners
      if (updatedWinners[i] && updatedWinners[i].source === WinnerSource.OFFICIAL) {
        continue;
      }

      // Only set winner if not already selected by user
      if (!updatedWinners[i] || !updatedWinners[i].team) {
        // Get teams for this matchup
        const team1Index = i * 2;
        const team2Index = i * 2 + 1;

        if (team1Index < initialTeams.length && team2Index < initialTeams.length) {
          const team1 = initialTeams[team1Index];
          const team2 = initialTeams[team2Index];

          const seed1 = team1.seed;
          const seed2 = team2.seed;

          // Higher seed number means lower seed (underdog)
          if (seed1 > seed2) {
            updatedWinners[i] = {
              team: team1.name,
              source: WinnerSource.USER
            };
          } else if (seed2 > seed1) {
            updatedWinners[i] = {
              team: team2.name,
              source: WinnerSource.USER
            };
          }
          // If seeds are equal, no winner is set
        }
      }
    }

    // Clear subsequent rounds and propagate winners through the bracket
    updatedWinners = clearSubsequentRounds(updatedWinners);
    updatedWinners = advanceWinnersToNextRoundsLowerSeeds(updatedWinners);

    setWinnersWithMetadata(updatedWinners);
    setForceArrayUpdate(prev => prev + 1);
    setHasUserMadeChanges(true);
    setTimeout(() => setErrorMessage(null), 3000);
  }, [winnersWithMetadata, clearSubsequentRounds, advanceWinnersToNextRoundsLowerSeeds]);

  // NEW: Implement Clear Bracket functionality
  const handleClearBracket = useCallback(() => {
    // Create new array with only official winners
    const clearedWinners: WinnerWithMetadata[] = [];
    
    for (let i = 0; i < winnersWithMetadata.length; i++) {
      const winner = winnersWithMetadata[i];
      if (winner && 
          winner.team && 
          winner.source === WinnerSource.OFFICIAL &&
          winner.team.trim() !== "" && 
          winner.team !== "xxx") {
        clearedWinners[i] = winner;
      }
    }
    
    setWinnersWithMetadata(clearedWinners);
    setForceArrayUpdate(prev => prev + 1);
    setHasUserMadeChanges(true);
    //setErrorMessage("Bracket cleared, official winners preserved");
    //setTimeout(() => setErrorMessage(null), 3000);
  }, [winnersWithMetadata]);

  // Handle winners update from UnifiedPoolScenarioBar
  const handleWinnersUpdate = useCallback((winners: WinnerWithMetadata[], error?: string) => {
    
    setWinnersWithMetadata(winners);
    setForceArrayUpdate(prev => prev + 1);
    
    if (error) {
      setErrorMessage(error);
    }
    
    setHasUserMadeChanges(false);
  }, []);

  // Handle validation errors from UnifiedPoolScenarioBar
  const handleValidationError = useCallback((error: string | null) => {
    setErrorMessage(error);
  }, []);

  // Handle user interactions in BracketVisualizer
  const handleUserBracketChanges = useCallback((updatedWinners: WinnerWithMetadata[]) => {
    setWinnersWithMetadata(updatedWinners);
    setForceArrayUpdate(prev => prev + 1);
    setHasUserMadeChanges(true);
    setErrorMessage(null);
  }, []);

  // Handle player scenario loading from GeneratedStandings
  const handlePlayerScenarioLoad = useCallback((winners: WinnerWithMetadata[], playerName: string, validationError?: string) => {
    setWinnersWithMetadata(winners);
    setForceArrayUpdate(prev => prev + 1);
    
    if (validationError) {
      setErrorMessage(validationError);
    } 
    
    setHasUserMadeChanges(true);
  }, []);
  
  // Handle player picks change
  const handlePlayerPicksChange = useCallback((picks: string) => {
    setPlayerPicks(picks);
  }, []);
  
  // Handle player selection for rooting interests
  const handlePlayerSelect = (playerName: string) => {
    if (isBracketComplete(knownWinnersArray)) return;
    setSelectedPlayer(playerName);
  };
  
  // Close rooting interests modal
  const handleCloseRootingInterests = () => {
    setSelectedPlayer(null);
  };

  // Handle loading a saved bracket (backward compatibility)
  const handleLoadBracket = useCallback((loadedWinners: string[]) => {
    // Convert string array to WinnerWithMetadata array
    const winnersWithMeta: WinnerWithMetadata[] = loadedWinners.map((team, index) => {
      if (team && team.trim() !== '' && team !== 'xxx') {
        return {
          team: team.trim(),
          source: 'user' as const
        };
      }
      return undefined as any;
    }).filter(Boolean);
    
    setWinnersWithMetadata(winnersWithMeta);
    setForceArrayUpdate(prev => prev + 1);
    setHasUserMadeChanges(true);
  }, []);

  // Handle error dismissal
  const handleErrorDismiss = () => {
    setErrorMessage(null);
  };
  
  return (
    <ConfigProvider>
      <ResponsiveLayout
        winnersWithMetadata={winnersWithMetadata}
        validationError={null}
        playerPicks={playerPicks}
        playerNames={playerNames}
        selectedPlayer={selectedPlayer}
        errorMessage={errorMessage}
        hasUserMadeChanges={hasUserMadeChanges}
        forceArrayUpdate={forceArrayUpdate}
        onWinnersUpdate={handleWinnersUpdate}
        onValidationError={handleValidationError}
        onUserBracketChanges={handleUserBracketChanges}
        onLoadPlayerScenario={handlePlayerScenarioLoad}
        onPlayerPicksChange={handlePlayerPicksChange}
        onPlayersExtracted={setPlayerNames}
        onPlayerSelect={handlePlayerSelect}
        onCloseRootingInterests={handleCloseRootingInterests}
        onLoadBracket={handleLoadBracket}
        onErrorDismiss={handleErrorDismiss}
        onFillWithHigherSeeds={handleFillWithHigherSeeds}
        onFillWithLowerSeeds={handleFillWithLowerSeeds}
        onClearBracket={handleClearBracket}
      />
    </ConfigProvider>
  );
};

export default App;